import type {CSSProperties, FC} from 'react';
import {useEffect} from 'react';
import {observer} from 'mobx-react';
import {InView} from 'react-intersection-observer';
import {Paper} from '@wellstone-solutions/web';
import {useStores} from 'hooks/useStores';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import {ConversationListItem} from './ConversationListItem';
import {ConversationListEmpty} from './ConversationListEmpty';
import type {Channel, ChannelCollection} from '../../types';

interface ConversationListProps {
  onSelect: (channel: Channel) => void;
  selectedChannel: Channel | null;
  channelCollection: ChannelCollection;
}

export const ConversationList: FC<ConversationListProps> = observer(
  ({onSelect, selectedChannel, channelCollection}) => {
    const {channelStore, organizationStore} = useStores();

    const loadNextPage = () =>
      channelStore.loadChannels(
        false,
        channelCollection === 'unread',
        organizationStore.organizationId,
      );

    //On mount get initial set of channels
    useEffect(() => {
      loadNextPage();
    }, []);

    const isEmpty =
      channelStore.isLoading === false && channelStore.channels.length === 0;

    return (
      <Paper>
        <div style={styles.scrollBox}>
          {channelStore.channels.map((channel: Channel) => (
            <ConversationListItem
              key={channel.id}
              channel={channel}
              onSelect={onSelect}
              isSelected={
                !!selectedChannel && selectedChannel.id === channel.id
              }
            />
          ))}

          {isEmpty && <ConversationListEmpty variant={channelCollection} />}

          {channelStore.hasMore && (
            <InView
              key={channelStore.channels.length}
              style={styles.inView}
              onChange={(inView) => {
                if (inView && !channelStore.isLoading) {
                  loadNextPage();
                }
              }}
            />
          )}

          {channelStore.isLoading && (
            <LoadingIndicator stylesOverride={{mb: 0}} size={24} />
          )}
        </div>
      </Paper>
    );
  },
);

const styles: Record<string, CSSProperties> = {
  scrollBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    height: '492px',
    overflowY: 'auto',
    padding: '24px',
  },
  inView: {
    height: '1px',
    flexShrink: 0,
  },
};
